import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { API_BASE } from "@/App";
import {
  BookOpen, ChevronRight, ChevronLeft, Loader2, Lock,
  FileText, Sparkles, ArrowRight, Search, BookMarked
} from "lucide-react";
import { Input } from "@/components/ui/input";
import ReactMarkdown from "react-markdown";

const MaterialHubPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { subject: urlSubject, chapterNum } = useParams();
  
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(urlSubject || null);
  const [chapters, setChapters] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState(chapterNum ? parseInt(chapterNum) : null);
  const [chapterContent, setChapterContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  const isPro = user?.role === "PRO";
  const examParam = user ? (localStorage.getItem(`clarias_exam_${user.id}`) || "UPSC") : "UPSC";
  const isSI = examParam === "SI";

  useEffect(() => {
    fetchSubjects();
  }, []);

  useEffect(() => {
    if (selectedSubject) {
      fetchChapters(selectedSubject);
    }
  }, [selectedSubject]);

  useEffect(() => {
    if (selectedSubject && selectedChapter) {
      fetchChapterContent(selectedSubject, selectedChapter);
    }
  }, [selectedSubject, selectedChapter]);

  const fetchSubjects = async () => {
    try {
      const { data } = await managedAxios.get(`${API_BASE}/material-hub/subjects?exam=${examParam}`);
      setSubjects(data.subjects);
    } catch (err) {
      console.error("Error fetching subjects:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchChapters = async (subject) => {
    try {
      const { data } = await managedAxios.get(`${API_BASE}/material-hub/${subject}/chapters?exam=${examParam}`);
      setChapters(data.chapters);
    } catch (err) {
      console.error("Error fetching chapters:", err);
    }
  };

  const fetchChapterContent = async (subject, chapterNum) => {
    setContentLoading(true);
    try {
      const { data } = await managedAxios.get(`${API_BASE}/material-hub/${subject}/chapter/${chapterNum}`);
      setChapterContent(data);
    } catch (err) {
      console.error("Error fetching chapter content:", err);
    } finally {
      setContentLoading(false);
    }
  };

  const filteredChapters = chapters.filter(ch => 
    ch.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Subject Selection View
  if (!selectedSubject) {
    return (
      <div className="min-h-screen bg-slate-50/30">
        <div className="max-w-6xl mx-auto px-6 py-10 space-y-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                  <BookMarked className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">📚 Material Hub</h1>
                  <p className="text-slate-500 text-sm">{isSI ? "Curated study material for SI" : "AI-powered study material for UPSC Prelims"}</p>
                </div>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate("/prelims")}>
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to Prelims
            </Button>
          </div>

          {/* Info Card */}
          <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-indigo-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-indigo-900">{isSI ? "Original Curated Content" : "AI-Rewritten Content"}</p>
                  <p className="text-sm text-indigo-700">
                    {isSI
                      ? "Exam-focused chapters written specifically for the SI syllabus. Original content — nothing copied."
                      : "All content is freshly rewritten by AI for better understanding and UPSC relevance. No copied content - everything is original and exam-focused."}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Subjects Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {subjects.map((subject) => (
                <Card
                  key={subject.id}
                  className="cursor-pointer hover:shadow-lg transition-all duration-300 border-0 overflow-hidden"
                  onClick={() => setSelectedSubject(subject.id)}
                >
                  <CardContent className="p-6">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${subject.color} flex items-center justify-center text-2xl mb-4`}>
                      {subject.icon}
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1">{subject.name}</h3>
                    <p className="text-sm text-slate-500 mb-3">{subject.description}</p>
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="text-xs">
                        {subject.chapters} Chapters
                      </Badge>
                      <Badge className="bg-indigo-100 text-indigo-700 text-xs">
                        {subject.exam_relevance}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {subjects.length === 0 && (
                <Card className="col-span-full">
                  <CardContent className="p-8 text-center">
                    <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No study material available yet.</p>
                    <p className="text-sm text-slate-400">More subjects coming soon!</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Chapter Content View
  if (selectedChapter && chapterContent) {
    return (
      <div className="min-h-screen bg-slate-50/30">
        <div className="max-w-4xl mx-auto px-6 py-10 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <Button 
              variant="outline" 
              onClick={() => {
                setSelectedChapter(null);
                setChapterContent(null);
              }}
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to Chapters
            </Button>
            
            <div className="flex items-center gap-2">
              {selectedChapter > 1 && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedChapter(selectedChapter - 1)}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Prev
                </Button>
              )}
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setSelectedChapter(selectedChapter + 1)}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Content Card */}
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              {contentLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <Loader2 className="w-10 h-10 animate-spin text-indigo-500 mb-4" />
                  <p className="text-slate-500">Generating fresh content...</p>
                  <p className="text-sm text-slate-400">This may take a moment</p>
                </div>
              ) : (
                <>
                  <div className="mb-6 pb-6 border-b">
                    <Badge className="bg-indigo-100 text-indigo-700 mb-3">
                      Chapter {chapterContent.chapter_number}
                    </Badge>
                    <h1 className="text-2xl font-bold text-slate-900">
                      {chapterContent.chapter_title}
                    </h1>
                    {chapterContent.is_rewritten && (
                      <div className="flex items-center gap-2 mt-3 text-sm text-emerald-600">
                        <Sparkles className="w-4 h-4" />
                        <span>AI-rewritten for clarity & UPSC relevance</span>
                      </div>
                    )}
                  </div>

                  {/* Markdown Content */}
                  <div className="prose prose-slate max-w-none prose-headings:text-slate-900 prose-p:text-slate-600 prose-li:text-slate-600 prose-strong:text-slate-900">
                    <ReactMarkdown>{chapterContent.content}</ReactMarkdown>
                  </div>

                  {/* Paywall for FREE users */}
                  {chapterContent.is_partial && chapterContent.paywall_message && (
                    <div className="mt-8 p-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200">
                      <div className="flex items-start gap-4">
                        <Lock className="w-6 h-6 text-indigo-600" />
                        <div>
                          <p className="font-semibold text-indigo-900">{chapterContent.paywall_message}</p>
                          <Button 
                            className="mt-4 bg-gradient-to-r from-indigo-500 to-purple-500"
                            onClick={() => navigate("/upgrade")}
                          >
                            Upgrade to PRO
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Chapter List View
  return (
    <div className="min-h-screen bg-slate-50/30">
      <div className="max-w-6xl mx-auto px-6 py-10 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setSelectedSubject(null);
                setChapters([]);
              }}
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 capitalize">
                {subjects.find(s => s.id === selectedSubject)?.name || selectedSubject}
              </h1>
              <p className="text-slate-500 text-sm">{chapters.length} chapters available</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search chapters..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Chapters List */}
        <div className="grid gap-3">
          {filteredChapters.map((chapter) => (
            <Card
              key={chapter.number}
              className="cursor-pointer hover:shadow-md transition-all duration-200 border-slate-200"
              onClick={() => setSelectedChapter(chapter.number)}
            >
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold">
                    {chapter.number}
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900">{chapter.title}</h3>
                    {chapter.page_count && (
                      <p className="text-xs text-slate-400">{chapter.page_count} pages</p>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MaterialHubPage;
